'use client'

import { atom } from 'jotai'
import { atomFamily } from 'jotai-family'
import { selectAtom } from 'jotai/utils'

import { getPositionRowKey } from '@/lib/position-row-key'
import { exchangeAtom } from '@/state/storage'
import { AlgoState } from '@/types/generated/algo'
import { AccountFees, AccountSummary, PortfolioData } from '@/types/generated/common/account'
import { LiquidityType, Position } from '@/types/generated/common/trading'
import { Liquidation } from '@/types/generated/common/types'
import { UnifiedOrderItem } from '@/types/generated/public_api'

// A trade-history row. Trades are surfaced through the same `UnifiedOrderItem`
// shape the order tables use (a synthetic `order`), but the `Order` proto can't
// carry the per-trade fee or the maker/taker liquidation type — so we hang them
// off the row as a sidecar. Extends `UnifiedOrderItem`, so anything reading
// `item.order` keeps working unchanged.
export interface TradeHistoryItem extends UnifiedOrderItem {
  fee?: string
  feeCurrency?: string
  liquidation?: Liquidation
  // Maker/taker for this fill. The fill's own order type (Trade.order_type)
  // rides on the synthetic `order.orderType`.
  liquidityType?: LiquidityType
}

export const accountDataLoadingAtom = atom({
  balances: true,
  positions: true,
  orders: true,
  trades: true,
})

// Raw atoms (store all data regardless of accountId)
// These are used internally by AccountDataManager to store unfiltered data
export const balancesRawAtom = atom<PortfolioData[]>([])
export const positionsRawAtom = atom<Position[]>([])
export const openOrdersRawAtom = atom<UnifiedOrderItem[]>([])
export const orderHistoryRawAtom = atom<UnifiedOrderItem[]>([])
export const tradesRawAtom = atom<TradeHistoryItem[]>([])

// FEES stream: latest AccountFees per account (latest-only snapshot, keyed by accountId).
export const accountFeesByIdAtom = atom<Record<string, AccountFees>>({})

// SUMMARY stream: latest AccountSummary per account (latest-only snapshot, keyed
// by accountId). Authoritative leverage / equity / PnL roll-up from public-api.
export const accountSummaryByIdAtom = atom<Record<string, AccountSummary>>({})

// Cross-account aggregate: every active account's positions/balances. Fed by
// <ActivePositionsSync />/<ActiveAccountSnapshotSync /> (one subscription per active
// account — see service/ws/account-positions-sync.tsx / account-snapshot-sync.tsx).
export const allPositionsAtom = atom((get) => get(positionsRawAtom))
export const allBalancesAtom = atom((get) => get(balancesRawAtom))

// Cheap primitive derived atom — lets badge/boolean consumers (e.g. a tab's position
// count, or "are there any positions to hedge") react only when the count itself
// changes, instead of subscribing to the whole array and re-rendering on every WS
// tick regardless of whether the count moved.
export const positionCountAtom = selectAtom(allPositionsAtom, (positions) => positions.length)

// Per-row lookup index, rebuilt once per `positionsRawAtom` write (O(n)) instead of
// each row doing its own O(n) `.find()` over the full array on every WS tick.
// No ref-count/eviction here (unlike optionIncrementalTickerAtomFamily in
// market-data-cache.ts) — positions are a small, bounded set per account, not an
// unbounded per-symbol-across-history cache, so the added complexity isn't justified.
const positionIndexesAtom = atom((get) => {
  const byRowKey = new Map<string, Position>()

  for (const p of get(positionsRawAtom)) {
    byRowKey.set(getPositionRowKey(p.accountId, p.exchange, p.symbol), p)
  }

  return { byRowKey }
})

export const positionByRowKeyAtomFamily = atomFamily((rowKey: string) =>
  selectAtom(positionIndexesAtom, (idx) => idx.byRowKey.get(rowKey) ?? null)
)

const EMPTY_POSITIONS: Position[] = []

function arePositionArraysEqual(a: Position[], b: Position[]) {
  if (a === b) return true
  if (a.length !== b.length) return false
  return a.every((p, i) => p === b[i])
}

/** Separator for `positionsByRowKeysKeyAtomFamily`'s key — never part of a row key. */
export const ROW_KEYS_KEY_SEPARATOR = '\n'

// Positions whose row keys (`getPositionRowKey`) are in a given set, keyed by the sorted keys
// joined with ROW_KEYS_KEY_SEPARATOR. A strategy group can span accounts and venues (one
// group may hold the same symbol from two accounts), so legs are identified by row key —
// never by symbol alone, nor scoped to a single account.
export const positionsByRowKeysKeyAtomFamily = atomFamily((rowKeysKey: string) =>
  selectAtom(
    allPositionsAtom,
    (positions) => {
      if (!rowKeysKey) return EMPTY_POSITIONS
      const rowKeys = new Set(rowKeysKey.split(ROW_KEYS_KEY_SEPARATOR))
      return positions.filter((p) =>
        rowKeys.has(getPositionRowKey(p.accountId, p.exchange, p.symbol))
      )
    },
    arePositionArraysEqual
  )
)

export const openOrdersAtom = atom(
  (get) => {
    const exchange = get(exchangeAtom)
    const allOrders = get(openOrdersRawAtom)
    if (!exchange?.accountId) return []
    return allOrders.filter(
      (item) => !item.order?.accountId || item.order.accountId === exchange.accountId
    )
  },
  (get, set, update: UnifiedOrderItem[] | ((prev: UnifiedOrderItem[]) => UnifiedOrderItem[])) => {
    const newValue = typeof update === 'function' ? update(get(openOrdersRawAtom)) : update
    set(openOrdersRawAtom, newValue)
  }
)

// Algo states (TWAP and other algo orders) from WS stream
export const algoStatesRawAtom = atom<AlgoState[]>([])

export const algoStatesAtom = atom(
  (get) => {
    const exchange = get(exchangeAtom)
    const all = get(algoStatesRawAtom)
    if (!exchange?.accountId) return []
    return all.filter((s) => !s.accountIds.length || s.accountIds.includes(exchange.accountId))
  },
  (get, set, update: AlgoState[] | ((prev: AlgoState[]) => AlgoState[])) => {
    const newValue = typeof update === 'function' ? update(get(algoStatesRawAtom)) : update
    set(algoStatesRawAtom, newValue)
  }
)

// Cross-account aggregates: every active account's rows, unfiltered by the
// currently-selected exchangeAtom account. Fed by <ActiveOrdersSync /> /
// <ActiveTradesSync /> (one subscription per active account — see
// service/ws/account-orders-sync.tsx), mirroring allPositionsAtom above.
// Used by the Open Orders / Order History / Trade History widgets specifically;
// everything else keeps reading the filtered atoms above for "current account"
// semantics.
export const allOpenOrdersAtom = atom((get) => get(openOrdersRawAtom))
export const allOrderHistoryAtom = atom((get) => get(orderHistoryRawAtom))
export const allTradesAtom = atom((get) => get(tradesRawAtom))
export const allAlgoStatesAtom = atom((get) => get(algoStatesRawAtom))

// Pagination state for history tables. One `useInfiniteQuery` per data type
// now covers every active account at once (see <AccountOrdersCoordinator />/
// <AccountTradesCoordinator /> in service/ws/account-orders-sync.tsx), so this
// is written directly from that single query — no more per-account keying or
// cross-account aggregation needed.
export interface HistoryPaginationState {
  fetchMore: (() => void) | null
  hasMore: boolean
  isFetching: boolean
}

const DEFAULT_PAGINATION: HistoryPaginationState = {
  fetchMore: null,
  hasMore: false,
  isFetching: false,
}

export const orderHistoryPaginationAtom = atom<HistoryPaginationState>(DEFAULT_PAGINATION)

export const tradesPaginationAtom = atom<HistoryPaginationState>(DEFAULT_PAGINATION)

// Per-account initial-dump completion, written by <AccountOrdersSync />/
// <AccountTradesSync /> and aggregated by <ActiveOrdersSync />/<ActiveTradesSync />
// into the global accountDataLoadingAtom.orders/.trades flags — only cleared once
// every currently-active account has completed its first dump, not just the
// first account to finish (see service/ws/account-orders-sync.tsx).
export const ordersLoadedAccountIdsAtom = atom<Record<string, boolean>>({})
export const tradesLoadedAccountIdsAtom = atom<Record<string, boolean>>({})

// Same pattern as the two atoms above, for Balances — written by
// <AccountSnapshotSync /> and aggregated by <ActiveAccountSnapshotSync /> into
// accountDataLoadingAtom.balances (see service/ws/account-snapshot-sync.tsx).
export const balancesLoadedAccountIdsAtom = atom<Record<string, boolean>>({})

// Account-keyed total equity from the venue's PORTFOLIO account total. null = the
// venue can't value the account right now (unknown); absent = nothing arrived yet.
export const totalBalanceUsdRawAtom = atom<Record<string, number | null>>({})

/**
 * Resync generation counter. Each increment tells every account sync consumer
 * that a fresh initial dump is on its way and their per-account dump trackers
 * must be reset.
 *
 * Contract: this atom signals *dump-flag reset only*. It does not reconnect the
 * socket — the recovery controller owns that through the WebSocket provider's
 * restart(). Reintroducing a disconnect/connect here would restart the socket
 * twice per recovery.
 *
 * Write it through beginResync() in service/runtime-health/resync-acks.ts, which
 * owns both the increment and the generation bookkeeping, rather than setting it
 * directly — the value doubles as the generation that acks are matched against.
 */
export const triggerResyncAtom = atom(0)
